// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { useEffect, useMemo } from 'react';
import { StyleSheet } from 'react-native';

import { View } from '@/components/Themed';

/**
 * Renders a presentation from the string it ships as, in an iframe fed from a blob URL.
 *
 * The document is created in this page's memory and revoked when the viewer unmounts, so it never
 * exists at an address the server could answer for: the only way to it is through the gated
 * screen that mounts this. The frame is sandboxed to same-origin without scripts — the decks are
 * static HTML and inline SVG, and their in-page anchors and Google Fonts stylesheet need nothing
 * more.
 */
export function DeckViewer({ html, title }: { html: string; title: string }) {
  const url = useMemo(() => URL.createObjectURL(new Blob([html], { type: 'text/html' })), [html]);
  useEffect(() => () => URL.revokeObjectURL(url), [url]);

  return (
    <View style={styles.frame} testID="deck-viewer">
      <iframe src={url} title={title} style={iframeStyle} sandbox="allow-same-origin" />
    </View>
  );
}

const iframeStyle: React.CSSProperties = { border: 0, width: '100%', height: '100%' };

const styles = StyleSheet.create({
  frame: { flex: 1, minHeight: 480 },
});
